import { useCallback, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { adminApi, adminKeys, type AdminOnboardingItem } from '@/features/admin/api/admin.api';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { toCsv } from '@/shared/utils/csv';
import { downloadCsvFile } from '@/shared/utils/downloadFile';
import { runWithConcurrency } from '@/shared/utils/concurrency';
import { extractApiError } from '@/shared/lib/extractApiError';
import { OnboardingStage } from '@/types';
import {
  SPRINGVERIFY_COLUMNS,
  hasUnusablePhone,
  isAwaitingSpringVerifySend,
  isMissingContactDetails,
  onboardingRowName,
  springVerifyFilename,
  toSpringVerifyRow,
} from '@/features/admin/constants/springVerifyExport';

/**
 * Bulk-export the background-check queue to SpringVerify, then flag the
 * exported applicants as sent.
 *
 * The ordering is the whole design. The file is written to disk *before* any
 * applicant is marked, because the two failure modes are not symmetric: an
 * unmarked applicant simply reappears in the next batch, whereas one marked as
 * sent without the file ever downloading is recorded as having been submitted
 * when they never were — and the lifecycle exposes no way back from
 * `in_progress` to `pending`. Do not reorder these steps.
 */

/** Simultaneous mark-as-sent requests. Enough to be quick, few enough to be polite. */
const MARK_CONCURRENCY = 4;

/** The download failed, so nothing was marked — the opposite of the usual advice. */
class DownloadFailed extends Error {}

export interface ExportOutcome {
  severity: 'success' | 'warning' | 'error' | 'info';
  message: string;
  /** Ids that could not be marked, for the retry affordance. */
  retryableIds?: string[];
}

interface ExportProgress {
  phase: 'fetching' | 'marking';
  done: number;
  total: number;
}

export function useSpringVerifyExport(stage: OnboardingStage, search: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [outcome, setOutcome] = useState<ExportOutcome | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<AdminOnboardingItem[] | null>(null);
  // Guards the keyboard-repeat case, where the disabled prop has not landed yet.
  const isRunning = useRef(false);

  const adminName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'an admin';

  const markNotes = useCallback(
    (batchStamp: string) =>
      `Exported to SpringVerify batch ${batchStamp} by ${adminName}`.slice(0, 2000),
    [adminName]
  );

  /** Step 1 — gather the batch and ask for confirmation. */
  const prepare = useMutation({
    mutationFn: async () => {
      setProgress({ phase: 'fetching', done: 0, total: 0 });
      const rows = await adminApi.fetchAllOnboarding({
        stage,
        search: search || undefined,
        onProgress: (done, total) => setProgress({ phase: 'fetching', done, total }),
      });
      return rows.filter(isAwaitingSpringVerifySend);
    },
    onSuccess: (eligible) => {
      setProgress(null);
      if (eligible.length === 0) {
        setOutcome({
          severity: 'info',
          message: search
            ? `No applicants matching "${search}" are waiting to be sent to SpringVerify.`
            : 'Everyone in Background Check has already been sent to SpringVerify.',
        });
        return;
      }
      setPendingConfirm(eligible);
    },
    onError: (error) => {
      setProgress(null);
      setOutcome({ severity: 'error', message: extractApiError(error).message });
    },
  });

  /** Step 2 — download, then mark. Only ever called from the confirm dialog. */
  const run = useMutation({
    mutationFn: async (rows: AdminOnboardingItem[]) => {
      const stamp = dayjs().format('YYYY-MM-DD HH:mm');

      // Download first. If this throws, nothing has been marked and the admin
      // can simply try again — but the failure has to be distinguishable from a
      // marking failure, because the advice afterwards is the opposite.
      try {
        downloadCsvFile(
          toCsv([SPRINGVERIFY_COLUMNS, ...rows.map(toSpringVerifyRow)]),
          springVerifyFilename()
        );
      } catch (cause) {
        throw new DownloadFailed(extractApiError(cause).message);
      }

      setProgress({ phase: 'marking', done: 0, total: rows.length });
      let done = 0;

      const results = await runWithConcurrency(rows, MARK_CONCURRENCY, async (row) => {
        const result = await adminApi.startVerification(row.id, markNotes(stamp));
        done += 1;
        setProgress({ phase: 'marking', done, total: rows.length });
        return result;
      });

      const failed = results.filter((result) => {
        // A 409 means someone else already moved this applicant on. The
        // outcome we wanted is the outcome we have, so it is not a failure.
        return result.status === 'rejected' && extractApiError(result.reason).status !== 409;
      });

      return { rows, failed: failed.map((result) => result.item) };
    },
    onSuccess: ({ rows, failed }) => {
      setProgress(null);
      setOutcome(buildOutcome(rows, failed));
      // Once, after the whole batch: every row has moved tab and the counts
      // have changed, so there is nothing left worth keeping cached.
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
    onError: (error) => {
      setProgress(null);
      setOutcome({
        severity: 'error',
        message:
          error instanceof DownloadFailed
            ? `The file could not be downloaded (${error.message}). Nobody was marked as sent, so nothing is out of step — try again.`
            : `The file downloaded, but nothing was marked as sent (${extractApiError(error).message}). Do not upload it twice — retry marking, or export again.`,
      });
    },
  });

  const start = useCallback(() => {
    if (isRunning.current) return;
    isRunning.current = true;
    setOutcome(null);
    prepare.mutate(undefined, { onSettled: () => (isRunning.current = false) });
  }, [prepare]);

  const confirm = useCallback(() => {
    const rows = pendingConfirm;
    setPendingConfirm(null);
    if (rows) run.mutate(rows);
  }, [pendingConfirm, run]);

  /** Re-marks only the applicants that failed. `start` is idempotent, so this is safe. */
  const retryMarking = useCallback(
    (ids: string[]) => {
      const stamp = dayjs().format('YYYY-MM-DD HH:mm');
      setOutcome(null);
      setProgress({ phase: 'marking', done: 0, total: ids.length });

      void runWithConcurrency(ids, MARK_CONCURRENCY, (id) =>
        adminApi.startVerification(id, markNotes(stamp))
      ).then((results) => {
        const stillFailing = results
          .filter((r) => r.status === 'rejected' && extractApiError(r.reason).status !== 409)
          .map((r) => r.item);

        setProgress(null);
        setOutcome(
          stillFailing.length === 0
            ? { severity: 'success', message: `Marked ${ids.length} applicant(s) as sent.` }
            : {
                severity: 'error',
                message: `${stillFailing.length} of ${ids.length} still could not be marked.`,
                retryableIds: stillFailing,
              }
        );
        queryClient.invalidateQueries({ queryKey: adminKeys.all });
      });
    },
    [markNotes, queryClient]
  );

  return {
    start,
    confirm,
    cancel: () => setPendingConfirm(null),
    retryMarking,
    pendingConfirm,
    progress,
    outcome,
    dismissOutcome: () => setOutcome(null),
    isBusy: prepare.isPending || run.isPending || progress !== null,
  };
}

/** Turn the batch result into one sentence the admin can act on. */
function buildOutcome(
  rows: AdminOnboardingItem[],
  failed: AdminOnboardingItem[]
): ExportOutcome {
  const notes: string[] = [];

  const incomplete = rows.filter(isMissingContactDetails);
  if (incomplete.length > 0) {
    notes.push(
      `${incomplete.length} are missing an email or phone and SpringVerify may reject them: ${names(incomplete)}.`
    );
  }

  const badPhones = rows.filter(hasUnusablePhone);
  if (badPhones.length > 0) {
    notes.push(
      `${badPhones.length} have a phone number that is not an Indian mobile — check the format for: ${names(badPhones)}.`
    );
  }

  const duplicates = duplicateEmails(rows);
  if (duplicates.length > 0) {
    // SpringVerify charges per candidate, so a duplicate costs real money.
    notes.push(`Duplicate email(s) in this batch: ${duplicates.join(', ')}.`);
  }

  if (failed.length > 0) {
    return {
      severity: 'warning',
      message: [
        `Exported ${rows.length} applicant(s).`,
        `${rows.length - failed.length} marked as sent;`,
        `${failed.length} could not be marked and will appear in your next export: ${names(failed)}.`,
        ...notes,
      ].join(' '),
      retryableIds: failed.map((row) => row.id),
    };
  }

  return {
    severity: notes.length > 0 ? 'warning' : 'success',
    message: [
      `Exported ${rows.length} applicant(s) and marked them as sent to SpringVerify.`,
      ...notes,
    ].join(' '),
  };
}

function names(rows: AdminOnboardingItem[], max = 5): string {
  const shown = rows.slice(0, max).map(onboardingRowName).join(', ');
  return rows.length > max ? `${shown} and ${rows.length - max} more` : shown;
}

function duplicateEmails(rows: AdminOnboardingItem[]): string[] {
  const seen = new Set<string>();
  const repeated = new Set<string>();

  for (const row of rows) {
    const email = row.user?.email?.toLowerCase();
    if (!email) continue;
    if (seen.has(email)) repeated.add(email);
    seen.add(email);
  }

  return [...repeated];
}
